// OrderPage.js

import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Typography,
  CircularProgress,
  Box,
  Divider,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Button,
} from "@mui/material";
import { Schedule } from "@mui/icons-material";
import { format } from "date-fns";
import { zhTW } from "date-fns/locale";
import getOrder from "../api/order/getOrder";
import { useNavigate, useParams } from "react-router-dom";

const PAYMENT_STATUS = {
  Paid: "已付款",
  Refunded: "已退款",
};

const ORDER_STATUS = {
  Canceled: "訂單取消",
};

const POLLING_INTERVAL_MS = 3000;
const POLLING_DURATION_MS = 3 * 60 * 1000;

const isOrderCompleted = (order) =>
  order?.PaymentStatus === PAYMENT_STATUS.Paid;

const isOrderTerminalIncomplete = (order) =>
  order?.PaymentStatus === PAYMENT_STATUS.Refunded ||
  order?.Status === ORDER_STATUS.Canceled;

const loadOrder = async (orderId) => {
  const responseData = await getOrder(orderId);
  if (responseData.code !== "000" || !responseData.order) {
    throw new Error(responseData.message || "Unknown error occurred.");
  }
  return responseData.order;
};

const OrderResultPage = () => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pollingExpired, setPollingExpired] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const retryTimer = useRef(null);
  const navigate = useNavigate();
  const { orderId } = useParams();

  useEffect(() => {
    let cancelled = false;
    const pollingDeadline = Date.now() + POLLING_DURATION_MS;

    const fetchOrderDetails = async () => {
      retryTimer.current = null;
      let fetchedOrder = null;

      try {
        fetchedOrder = await loadOrder(orderId);
        if (!cancelled) {
          setOrder(fetchedOrder);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      }

      if (cancelled) {
        return;
      }
      setLoading(false);

      if (
        isOrderCompleted(fetchedOrder) ||
        isOrderTerminalIncomplete(fetchedOrder)
      ) {
        return;
      }

      const remainingTime = pollingDeadline - Date.now();
      if (remainingTime <= 0) {
        setPollingExpired(true);
        return;
      }

      retryTimer.current = window.setTimeout(
        fetchOrderDetails,
        Math.min(POLLING_INTERVAL_MS, remainingTime)
      );
    };

    setOrder(null);
    setLoading(true);
    setError(null);
    setPollingExpired(false);
    fetchOrderDetails();

    return () => {
      cancelled = true;
      if (retryTimer.current) {
        window.clearTimeout(retryTimer.current);
      }
    };
  }, [orderId]);

  const getDayInChinese = (date) => {
    const days = ["日", "一", "二", "三", "四", "五", "六"];
    const day = date.getDay();
    return days[day];
  };
  const formatDateToCustomString = (date) => {
    if (!(date instanceof Date)) {
      return "";
    }

    const monthDay = format(date, "MM/dd", { locale: zhTW });
    const chineseDay = getDayInChinese(date);
    const time = format(date, "HH:mm", { locale: zhTW });

    return `${monthDay}(${chineseDay}) ${time}`;
  };
  if (loading) {
    return <CircularProgress />;
  }
  const handleHomeClicked = () => {
    navigate("/app");
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      const fetchedOrder = await loadOrder(orderId);
      setOrder(fetchedOrder);
      setError(null);
      setPollingExpired(
        !isOrderCompleted(fetchedOrder) &&
          !isOrderTerminalIncomplete(fetchedOrder)
      );
    } catch (err) {
      setError(err.message);
      setPollingExpired(true);
    } finally {
      setRefreshing(false);
    }
  };

  const completed = isOrderCompleted(order);
  const terminalIncomplete = isOrderTerminalIncomplete(order);
  const resultTitle = completed
    ? "訂單已完成"
    : pollingExpired && error && !order
    ? "暫時無法確認訂單結果"
    : terminalIncomplete || pollingExpired
    ? "訂單尚未完成"
    : "訂單尚未完成，正在確認…";

  return (
    <Container maxWidth="md">
      <Box my={5}>
        <Typography variant="h4" align="center">
          {resultTitle}
        </Typography>
      </Box>
      {order && (
        <Grid item xs={12} sm={6}>
          <Card variant="outlined">
            <CardHeader title="訂位資訊" avatar={<Schedule />} />
            <Divider />
            <CardContent>
              <Typography variant="body1">
                店家名稱: {order.StoreName}
              </Typography>
              <Typography variant="body1">桌次: {order.TableName}</Typography>
              <Typography variant="body1">
                開始時間:{" "}
                {formatDateToCustomString(new Date(order.ReservationStartTime))}
              </Typography>
              <Typography variant="body1">
                結束時間:{" "}
                {formatDateToCustomString(new Date(order.ReservationEndTime))}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      )}

      <Box display="flex" justifyContent="center" gap={2} my={2}>
        {pollingExpired && !completed && !terminalIncomplete && (
          <Button
            color="info"
            variant="outlined"
            disabled={refreshing}
            onClick={handleRefresh}
          >
            {refreshing ? "查詢中…" : "重新查詢"}
          </Button>
        )}
        <Button color="info" variant="contained" onClick={handleHomeClicked}>
          回首頁
        </Button>
      </Box>
    </Container>
  );
};

export default OrderResultPage;
